import { Injectable, computed, inject, signal } from '@angular/core';
import { catchError, forkJoin, of, timer } from 'rxjs';

import { SavingArenaApiService } from '../api/saving-arena-api.service';
import {
  FrontendAssistantAnswer,
  FrontendAssistantContext,
  FrontendAssistantResults,
} from '../api/api.models';

/** One line in the transcript. `results` is only ever set on an assistant turn that found rows. */
export interface AssistantMessage {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  results?: FrontendAssistantResults;
  suggestions?: string[];
  /** True while this turn is still being answered — the transcript renders the thinking indicator. */
  pending?: boolean;
  failed?: boolean;
}

const EMPTY_RESULTS: FrontendAssistantResults = {
  stores: [],
  coupons: [],
  products: [],
  categories: [],
};

/**
 * The floor on how long an answer takes to appear.
 *
 * The brief asks for a visible "thinking" beat so the panel reads as an assistant rather than a
 * search box. The backend answers most questions in well under a second, so without this the reply
 * would snap in instantly. It is a FLOOR, not a fixed delay: `forkJoin` below waits for whichever
 * finishes last, so a slow query is never padded on top of — it just takes as long as it takes.
 */
const MIN_THINKING_MS = 1600;

/**
 * The conversation behind the homepage AI field and the chat drawer.
 *
 * Held at the root so both share one transcript: typing in the homepage field starts the
 * conversation, the drawer opens onto it, and every later message continues the same thread.
 *
 * There is no language model anywhere in this feature. The service sends the question to
 * `/api/frontend/assistant`, which answers it out of the SavingArena catalogue and hands back the
 * rows to render. Nothing shown to a visitor is generated text about a price, a code or a discount.
 */
@Injectable({ providedIn: 'root' })
export class AssistantService {
  private readonly api = inject(SavingArenaApiService);

  private readonly messagesSignal = signal<AssistantMessage[]>([]);
  private readonly openSignal = signal(false);
  private readonly busySignal = signal(false);
  private nextId = 1;

  /** What the previous answer told us the conversation is about. Sent with the next question. */
  private context: FrontendAssistantContext | null = null;

  readonly messages = this.messagesSignal.asReadonly();
  readonly isOpen = this.openSignal.asReadonly();
  readonly isBusy = this.busySignal.asReadonly();
  readonly hasConversation = computed(() => this.messagesSignal().length > 0);

  /** Opens the drawer without asking anything — used when the field is focused but not submitted. */
  open(): void {
    this.openSignal.set(true);
  }

  close(): void {
    this.openSignal.set(false);
  }

  /** Clears the thread and forgets the subject, so the next question starts fresh. */
  reset(): void {
    this.messagesSignal.set([]);
    this.context = null;
    this.busySignal.set(false);
  }

  /**
   * Asks a question and appends both turns to the transcript.
   *
   * The assistant turn is appended immediately in a `pending` state so the thinking indicator has
   * something to render, then filled in when the answer arrives.
   */
  ask(rawQuestion: string): void {
    const question = rawQuestion.trim();
    if (!question || this.busySignal()) return;

    this.openSignal.set(true);
    this.busySignal.set(true);

    const answerId = this.nextId + 1;
    this.messagesSignal.update((list) => [
      ...list,
      { id: this.nextId++, role: 'user', text: question },
      { id: this.nextId++, role: 'assistant', text: '', pending: true },
    ]);

    // Waits for BOTH the request and the minimum thinking beat, so the pause is a floor rather than
    // an added delay.
    forkJoin({
      answer: this.api.askAssistant(question, this.context).pipe(catchError(() => of(null))),
      _: timer(MIN_THINKING_MS),
    }).subscribe(({ answer }) => {
      const data = answer?.data as FrontendAssistantAnswer | undefined;
      this.busySignal.set(false);

      if (!data) {
        this.settle(answerId, {
          text: 'Sorry — I could not reach the catalogue just then. Please try that again.',
          failed: true,
        });
        return;
      }

      this.context = data.context ?? null;
      this.settle(answerId, {
        text: data.reply,
        results: data.results ?? EMPTY_RESULTS,
        suggestions: data.suggestions ?? [],
      });
    });
  }

  private settle(id: number, patch: Partial<AssistantMessage>): void {
    this.messagesSignal.update((list) =>
      list.map((message) => (message.id === id ? { ...message, ...patch, pending: false } : message)),
    );
  }
}
